import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import useAgencies from '@hooks/useAgencies'
import useAgencyProvider from '@hooks/useAgencyProvider'
import useCurrentUser from '@hooks/useCurrentUser'
import { generateUrls } from '@hooks/useProviderAccount'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ProviderAccountsTable from '../ProviderAccountsTable'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Agency Title or ID',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { providerAccountsIndexUrl } = generateUrls()

  const user = useCurrentUser()
  const { provider } = user

  const { callbacks: { createOrEditAgencyProvider } } = useAgencyProvider({ provider_id: provider?.id })

  const agenciesPayload = useAgencies({
    filters: { ...pageFilters },
    requestOptions: {
      include_agency_provider_account_for_provider: true,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    hasFilteredAgencies,
    loading,
  } = agenciesPayload

  return (
    <>
      <Helmet>
        <title>Provider Accounts | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="provider_accounts"
        tabBarItems={[
          {
            href: providerAccountsIndexUrl,
            icon: faCheckCircle,
            key: 'provider_accounts',
            title: 'Provider Accounts',
          },
        ]}
        title="Provider Accounts"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencies && <ListBlankState />}

            {!loading && hasFilteredAgencies && (
              <DashboardModule title="Provider Accounts">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    <ProviderAccountsTable filteredAgencies={filteredAgencies} />
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredAgenciesCount}
                    loading={loading}
                  >
                    <Button
                      buttonStyle="secondaryEdit"
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() => createOrEditAgencyProvider()}
                      // @ts-expect-error TS2322
                      size={['medium', 'small']}
                      width={['100%', 'fit-content']}
                    >
                      Add Agency
                    </Button>
                  </DashboardModule.LoadMoreFooter>
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
